import { Box, Column, RichTextViewer, Text } from '~/commonComponents';
import { InfoSection } from './components/InfoSection';
import { LabelValueDisplay } from './components/LabelValueDisplay';
import {
  formatPayTitle,
  formatPayRange,
  formatBenefits,
  formatExpectedDays,
} from './utils/formatters';
import { useMemo } from 'react';
import { EnumHelper } from '~/utils/EnumHelper';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { useGetOfficeById } from '~/hooks';
import { IDraftJobPostEntity } from '~/domain/PermanentJobs';

export interface IJobDetailsPreviewProps {
  /**
   * Job details data to display
   */
  data?: IDraftJobPostEntity;
  /**
   * Callback when edit button is clicked for General Job Information section
   */
  onEditGeneral?: () => void;
  /**
   * Callback when edit button is clicked for Schedule section
   */
  onEditSchedule?: () => void;
  /**
   * Callback when edit button is clicked for Job Description section
   */
  onEditDescription?: () => void;
}

/**
 * JobDetailsPreview - Display job details from draft data
 *
 * Fetches draft data using useDraftJobPostData hook and displays it in a read-only format.
 * Organized into three sections: General Job Information, Schedule, and Job Description.
 *
 * @example
 * ```tsx
 * <JobDetailsPreview
 *   draftId="draft_123"
 *   onEditGeneral={() => navigate('/edit?section=general')}
 *   onEditSchedule={() => navigate('/edit?section=schedule')}
 *   onEditDescription={() => navigate('/edit?section=description')}
 * />
 * ```
 */
export const JobDetailsPreview = ({
  onEditGeneral,
  onEditSchedule,
  onEditDescription,
  data,
}: IJobDetailsPreviewProps) => {
  const {
    officeId,
    title,
    professionType,
    subProfession,
    officeSpecialty,
    payType,
    minRate,
    maxRate,
    benefits,
    benefitsOther,
    scheduleType,
    expectedDays,
    description,
  } = data || {};

  // Get office details using the reusable hook
  const office = useGetOfficeById(officeId);
  const officeName = office?.office_name || '';

  // Format display values
  const payTitleDisplay = formatPayTitle(minRate, maxRate);
  const payRangeDisplay = formatPayRange(minRate, maxRate, payType);
  const benefitsDisplay = formatBenefits(benefits || []);
  const expectedDaysDisplay = formatExpectedDays(expectedDays || []);

  // Get profession display name (subProfession takes precedence)
  const professionDisplay = useMemo(() => {
    const profToDisplay = subProfession || professionType;
    if (!profToDisplay) return '';

    const isSubProfession = EnumHelper.getObjectKeys(UserSubProfession).includes(profToDisplay);
    return isSubProfession
      ? UserSubProfession.getName(profToDisplay)
      : UserProfession.getName(profToDisplay);
  }, [professionType, subProfession]);

  return (
    <Box>
      {/* General Job Information Section */}
      <Column gap={14}>
        <Text fontSize={22} bold>
          Job Details
        </Text>
        <InfoSection
          icon="briefcase-solid"
          title="General Job Information"
          rightActionIcon="edit-pencil"
          onRightActionPress={onEditGeneral}
        >
          <Column gap={14}>
            <LabelValueDisplay label="Office Name" value={officeName} />
            <LabelValueDisplay label="Job Title" value={title} />
            <LabelValueDisplay label="Professional Type" value={professionDisplay} />
            <LabelValueDisplay label="Specialty" value={officeSpecialty} />
            <LabelValueDisplay label="Pay Type" value={payType} />
            <LabelValueDisplay label={payTitleDisplay} value={payRangeDisplay} />
            <LabelValueDisplay label="Benefits" value={benefitsDisplay} />
            {benefitsOther && <LabelValueDisplay label="Other Benefits" value={benefitsOther} />}
          </Column>
        </InfoSection>
      </Column>

      {/* Schedule Section */}
      <Column marginTop={10}>
        <InfoSection
          icon="calendar-solid"
          title="Schedule"
          rightActionIcon="edit-pencil"
          onRightActionPress={onEditSchedule}
        >
          <Column gap={14}>
            <LabelValueDisplay label="Schedule Type" value={scheduleType} />
            <LabelValueDisplay label="Expected Days" value={expectedDaysDisplay} />
          </Column>
        </InfoSection>
      </Column>

      {/* Job Description Section */}
      <Column marginTop={10}>
        <InfoSection
          icon="document-solid"
          title="Job Description"
          rightActionIcon="edit-pencil"
          onRightActionPress={onEditDescription}
        >
          <Box gap={14}>
            <RichTextViewer content={description || ''} />
          </Box>
        </InfoSection>
      </Column>
    </Box>
  );
};
